import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiCheckCircle, FiAlertCircle, FiInfo } from 'react-icons/fi';

const ToastContext = createContext(null);

const TOAST_DURATION_MS = 4500;
const TOAST_EXIT_MS = 300;

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = 'info', title, description, action }) => {
    const id = Date.now() + Math.random();
    const duration = action ? 12000 : TOAST_DURATION_MS;
    setToasts((prev) => [...prev, { id, type, title, description: description || '', action: action || null, leaving: false }]);
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, TOAST_EXIT_MS);
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => {
      const toast = prev.find((t) => t.id === id);
      if (toast?.leaving) return prev;
      return prev.map((t) => (t.id === id ? { ...t, leaving: true } : t));
    });
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_EXIT_MS);
  }, []);

  const value = { addToast, removeToast };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="vissory-toast-container" aria-live="polite">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`vissory-toast vissory-toast--${t.type}${t.leaving ? ' vissory-toast--leaving' : ''}`}
              role="status"
            >
              <span className="vissory-toast__icon">
                {t.type === 'success' && <FiCheckCircle />}
                {t.type === 'error' && <FiAlertCircle />}
                {t.type === 'info' && <FiInfo />}
              </span>
              <div className="vissory-toast__text">
                <span className="vissory-toast__title">{t.title}</span>
                {t.description ? (
                  <span className="vissory-toast__description">{t.description}</span>
                ) : null}
              </div>
              {t.action && (
                <button
                  type="button"
                  className="vissory-toast__action"
                  onClick={() => {
                    t.action.onClick && t.action.onClick();
                    removeToast(t.id);
                  }}
                >
                  {t.action.label}
                </button>
              )}
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
